/**
 * API client for the FIL Portal backend (FastAPI @ :8000).
 *
 * Set REACT_APP_API_BASE to override the base URL. Defaults to the local
 * FastAPI dev server.
 *
 * The backend is the single source of truth: there is NO frontend mock/fallback
 * data anywhere. Every page fetches via `useApi` and renders loading/error
 * states. The `request` helper returns null only for genuinely empty bodies
 * (still-stubbed `pass` endpoints), which callers treat as an error/empty state.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

/** Send the user to the in-app login route. In cognito mode this renders the
 *  custom auth screens; in mock mode the /login page bounces to the backend dev
 *  chooser. Full navigation so it also works from non-React contexts (the 401
 *  handler below). */
export function redirectToLogin(): void {
  window.location.assign('/login');
}

/**
 * Log out in two steps. First POST /auth/logout (carries the X-Requested-With
 * CSRF header) so the sensitive step — destroying the app session cookie — cannot
 * be forged by a cross-site request. The backend returns the Cognito Hosted-UI
 * /logout URL; we then do a top-level navigation to it so the browser lands on
 * Cognito and terminates any Hosted-UI session (that teardown genuinely needs a
 * navigation, otherwise the next login silently re-authenticates the same user).
 */
export async function redirectToLogout(): Promise<void> {
  let logoutUrl = '/login';
  try {
    const res = await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
    });
    const data = await res.json().catch(() => null);
    if (data?.logoutUrl) logoutUrl = data.logoutUrl;
  } catch {
    /* Navigate to a safe default even if the POST failed. */
  }
  window.location.href = logoutUrl;
}

async function request<T>(path: string, options?: RequestInit): Promise<T | null> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    ...options,
  });
  if (res.status === 401) {
    // Session missing/expired — bounce to login (BFF handles the OAuth dance).
    redirectToLogin();
    throw new Error('Not authenticated');
  }
  if (!res.ok) {
    // Surface the backend's `detail` (FastAPI HTTPException) when present, so
    // callers can show a meaningful message instead of a bare status code.
    const detail = await res
      .clone()
      .json()
      .then((d) => (d && typeof d.detail === 'string' ? d.detail : null))
      .catch(() => null);
    throw new Error(detail || `API ${path} failed: ${res.status}`);
  }
  const text = await res.text();
  if (!text) return null; // stubbed endpoint (FastAPI `pass` returns empty body)
  return JSON.parse(text) as T;
}

/** POST that streams a file back and triggers a browser download. Returns the
 *  filename saved. Used for generated documents (e.g. the priorities workbook). */
async function downloadPost(path: string, body?: unknown): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    redirectToLogin();
    throw new Error('Not authenticated');
  }
  if (res.status === 502 || res.status === 504) {
    throw new Error('The report took too long to generate and the connection timed out. Please try again.');
  }
  if (!res.ok) throw new Error(`Report generation failed (${res.status}). Please try again.`);

  const disp = res.headers.get('Content-Disposition') || '';
  const match = disp.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'download';

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return filename;
}

export interface CurrentUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  needsProfile: boolean;
  role: 'applicant' | 'evaluator' | 'admin';
  fiId: string | null;
  applicationId: string | null;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// --- Typed endpoint helpers --------------------------------------------------

export const API = {
  // --- Auth ------------------------------------------------------------------
  me: () => api.get<CurrentUser>('/auth/me'),
  updateProfile: (firstName: string, lastName: string) =>
    api.put<CurrentUser>('/auth/profile', { firstName, lastName }),

  dashboardKpis: () => api.get<any>('/dashboard/kpis'),
  actions: (owner = 'me') => api.get<any[]>(`/actions?owner=${owner}`),
  fiActions: (fiId: string) => api.get<any[]>(`/actions?fi=${fiId}`),

  listFIs: (cycle = 'FY26') => api.get<any[]>(`/fis?cycle=${cycle}`),
  createFI: (body: unknown) => api.post<any>('/fis', body),
  getFI: (id: string) => api.get<any>(`/fis/${id}`),
  updateFI: (id: string, body: unknown) => api.put<any>(`/fis/${id}`, body),
  deleteFI: (id: string) => api.delete<any>(`/fis/${id}`),
  updateFIContacts: (id: string, body: unknown) => api.put<any>(`/fis/${id}/contacts`, body),
  onboardContact: (fiId: string, participantId: number, role: string) =>
    api.post<any>(`/fis/${fiId}/contacts/${participantId}/onboard`, { role }),
  revokeOnboardContact: (fiId: string, participantId: number) =>
    api.delete<any>(`/fis/${fiId}/contacts/${participantId}/onboard`),
  resendContactInvite: (fiId: string, participantId: number) =>
    api.post<any>(`/fis/${fiId}/contacts/${participantId}/resend-invite`),
  changeContactEmail: (fiId: string, participantId: number, email: string) =>
    api.put<any>(`/fis/${fiId}/contacts/${participantId}/email`, { email }),
  updateFILabYears: (id: string, body: unknown) => api.put<any>(`/fis/${id}/lab-years`, body),
  fiNotes: (id: string) => api.get<any>(`/fis/${id}/notes`),
  generateNotes: (id: string) => api.post<any>(`/fis/${id}/notes/generate`),

  inviteDraft: (fiId: string) => api.post<any>('/emails/invite-draft', { fiId }),
  inviteSend: (draftId: string) => api.post<any>(`/emails/invite-draft/${draftId}/send`),
  calDraft: (fiId: string) => api.post<any>('/emails/cal-draft', { fiId }),
  calSend: (draftId: string) => api.post<any>(`/emails/cal-draft/${draftId}/send`),
  thankYouDraft: (callId: string, fiId: string) =>
    api.post<any>('/emails/thank-you-draft', { callId, fiId }),
  thankYouSend: (draftId: string) => api.post<any>(`/emails/thank-you-draft/${draftId}/send`),

  getTranscript: (callId: string) => api.get<any>(`/calls/${callId}/transcript`),
  saveTranscript: (callId: string, transcript: string) =>
    api.put<any>(`/calls/${callId}/transcript`, { transcript }),
  analyzeCall: (callId: string, transcript: string) =>
    api.post<any>(`/calls/${callId}/analyze`, { transcript }),
  getReview: (callId: string) => api.get<any>(`/calls/${callId}/review`),
  saveReview: (callId: string, body: unknown) => api.put<any>(`/calls/${callId}/review`, body),

  scoring: (cycle?: string) => api.get<any>(`/priorities/scoring${cycle ? `?cycle=${cycle}` : ''}`),

  exportNotes: (body: unknown) => downloadPost('/exports/notes', body),
  exportPriorityMapping: (cycle?: string) => downloadPost('/exports/priority-mapping', { cycle }),
  exportPriorityReport: (cycle?: string) => downloadPost('/exports/priority-report', { cycle }),
  exportDeck: (body: unknown) => downloadPost('/exports/deck', body),
  deckChat: (body: unknown) => api.post<any>('/agent/deck-chat', body),

  // --- Phil platform (F6S replacement) ---------------------------------------
  // Applicant
  applicantDashboard: () => api.get<any>('/applicant/dashboard'),
  applicationProgress: (id = 'me') => api.get<any>(`/application/progress?id=${id}`),
  applicationSchema: () => api.get<any>('/application/schema'),
  applicationReview: () => api.get<any>('/application/review'),
  getSectionAnswers: (sectionId: string) => api.get<any>(`/application/section/${sectionId}`),
  saveSection: (sectionId: string, answers: unknown) =>
    api.post<any>(`/application/section/${sectionId}`, { answers }),
  uploadUrl: (questionId: number, filename: string, contentType: string) =>
    api.post<any>('/application/upload-url', { questionId, filename, contentType }),
  fileUrl: (key: string) => api.get<any>(`/application/file-url?key=${encodeURIComponent(key)}`),
  submitApplication: () => api.post<any>('/application/submit'),
  seedQuestions: (year: number) => api.post<any>(`/admin/questions/seed/${year}`),

  // Applicant: Resources section (materials uploaded by admins)
  resources: () => api.get<any>('/resources'),
  resourceFileUrl: (id: number) => api.get<any>(`/resources/${id}/file-url`),

  // Evaluator (FI)
  fiAssigned: () => api.get<any>('/fi/assigned'),
  fiPast: () => api.get<any>('/fi/past'),
  fiApplication: (id: string) => api.get<any>(`/fi/applications/${id}`),
  fiFileUrl: (id: string, key: string) =>
    api.get<any>(`/fi/applications/${id}/file-url?key=${encodeURIComponent(key)}`),
  fiScores: (id: string) => api.get<any>(`/fi/scores/${id}`),
  saveFiScores: (id: string, body: unknown) => api.post<any>(`/fi/scores/${id}`, body),

  // Administrator: content management
  getSettings: () => api.get<any>('/admin/settings'),
  updateSettings: (body: unknown) => api.put<any>('/admin/settings', body),
  adminQuestions: (year?: number) =>
    api.get<any>(`/admin/questions${year ? `?year=${year}` : ''}`),
  updateSectionQuestions: (sectionId: string, body: unknown, year?: number) =>
    api.put<any>(`/admin/questions/section/${sectionId}${year ? `?year=${year}` : ''}`, body),
  updateSections: (body: unknown, year?: number) =>
    api.put<any>(`/admin/questions/sections${year ? `?year=${year}` : ''}`, body),
  setActiveQuestionYear: (year: number) =>
    api.put<any>('/admin/questions/active-year', { year }),
  adminEvaluatorQuestions: (year?: number) =>
    api.get<any>(`/admin/evaluator-questions${year ? `?year=${year}` : ''}`),
  updateEvaluatorQuestions: (body: unknown, year?: number) =>
    api.put<any>(`/admin/evaluator-questions${year ? `?year=${year}` : ''}`, body),
  seedEvaluatorQuestions: (year: number) =>
    api.post<any>(`/admin/evaluator-questions/seed/${year}`),
  adminPipelineColumns: (year?: number) =>
    api.get<any>(`/admin/pipeline-columns${year ? `?year=${year}` : ''}`),
  updatePipelineColumns: (body: unknown, year?: number) =>
    api.put<any>(`/admin/pipeline-columns${year ? `?year=${year}` : ''}`, body),
  seedPipelineColumns: (year: number) =>
    api.post<any>(`/admin/pipeline-columns/seed/${year}`),
  getFIPriorities: (year?: number) =>
    api.get<any>(`/admin/fi-priorities${year ? `?year=${year}` : ''}`),
  updateFIPriorities: (fiId: string, body: unknown, year?: number) =>
    api.put<any>(`/admin/fi-priorities/${fiId}${year ? `?year=${year}` : ''}`, body),
  getPriorityCategories: () =>
    api.get<any>('/admin/priority-categories'),
  createPriorityCategory: (body: unknown) =>
    api.post<any>('/admin/priority-categories', body),
  updatePriorityCategory: (id: string, body: unknown) =>
    api.put<any>(`/admin/priority-categories/${encodeURIComponent(id)}`, body),
  deletePriorityCategory: (id: string) =>
    api.delete<any>(`/admin/priority-categories/${encodeURIComponent(id)}`),

  // Administrator: team access (who can reach the admin surface)
  listAdmins: () => api.get<any[]>('/admin/admins'),
  addAdmin: (email: string, firstName: string, lastName: string) =>
    api.post<any>('/admin/admins', { email, firstName, lastName }),
  removeAdmin: (id: number) => api.delete<any>(`/admin/admins/${id}`),
  resendAdminInvite: (id: number) => api.post<any>(`/admin/admins/${id}/resend-invite`),

  // Administrator: applicant resources (upload / manage)
  adminResources: () => api.get<any>('/admin/resources'),
  resourceUploadUrl: (filename: string, contentType: string) =>
    api.post<any>('/admin/resources/upload-url', { filename, contentType }),
  createResource: (body: unknown) => api.post<any>('/admin/resources', body),
  updateResource: (id: number, body: unknown) => api.put<any>(`/admin/resources/${id}`, body),
  deleteResource: (id: number) => api.delete<any>(`/admin/resources/${id}`),

  // Administrator (FIL): F6S application admin
  internalApplications: (cycle = 'FY26', includeArchived = false) =>
    api.get<any>(`/internal/applications?cycle=${cycle}${includeArchived ? '&include_archived=true' : ''}`),
  internalApplication: (id: string) => api.get<any>(`/internal/applications/${id}`),
  setApplicationAssignments: (id: string, fiIds: string[]) =>
    api.put<any>(`/internal/applications/${id}/assignments`, { fiIds }),
  setApplicationArchived: (id: string, archived: boolean) =>
    api.post<any>(`/internal/applications/${id}/archive`, { archived }),
  resetApplication: (id: string) =>
    api.post<any>(`/internal/applications/${id}/reset`, {}),
  deleteApplicationAccount: (id: string) =>
    api.delete<any>(`/internal/applications/${id}`),
  fiAssignments: (fiId: string) => api.get<any>(`/internal/fis/${fiId}/assignments`),
  setFiAssignments: (fiId: string, applicationIds: string[]) =>
    api.put<any>(`/internal/fis/${fiId}/assignments`, { applicationIds }),
  exportApplications: (body: unknown) => downloadPost('/export/applications', body),
  internalReports: () => api.get<any>('/internal/reports'),
  generateReport: (body: unknown) => api.post<any>('/reports/generate', body),
};

// --- Custom auth UI ----------------------------------------------------------
// The auth screens (login/signup/verify/forgot/reset/new-password) talk to these
// pre-auth endpoints. They deliberately do NOT use `request`, because a failed
// login must render an inline error — not trigger the 401 → redirect-to-login
// loop that `request` performs for the authenticated app.

export interface AuthConfig {
  mode: 'mock' | 'cognito';
  captchaSiteKey: string;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSymbols: boolean;
  };
}

/** Result of an auth POST: `ok` false carries a safe, user-facing `message`;
 *  `status` is the backend's flow signal (e.g. "unconfirmed", "confirm"). */
export interface AuthResult {
  ok: boolean;
  status?: string;
  message?: string;
}

async function authPost(path: string, body: unknown): Promise<AuthResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, message: 'Network error. Please check your connection and try again.' };
  }
  let data: any = null;
  try { data = await res.json(); } catch { /* empty/non-JSON body */ }
  if (!res.ok) {
    return { ok: false, message: (data && data.detail) || 'Something went wrong. Please try again.' };
  }
  return { ok: true, status: data?.status };
}

export const AuthAPI = {
  config: () => api.get<AuthConfig>('/auth/config'),
  login: (email: string, password: string) =>
    authPost('/auth/login', { email, password }),
  setNewPassword: (password: string) =>
    authPost('/auth/set-new-password', { password }),
  verifyMfa: (code: string) =>
    authPost('/auth/verify-mfa', { code }),
  signup: (email: string, password: string, captchaToken: string) =>
    authPost('/auth/signup', { email, password, captchaToken }),
  confirm: (email: string, code: string) =>
    authPost('/auth/confirm', { email, code }),
  resendCode: (email: string, captchaToken: string) =>
    authPost('/auth/resend-code', { email, captchaToken }),
  forgotPassword: (email: string, captchaToken: string) =>
    authPost('/auth/forgot-password', { email, captchaToken }),
  resetPassword: (email: string, code: string, password: string) =>
    authPost('/auth/reset-password', { email, code, password }),
};

export const API_BASE_URL = API_BASE;
